import { z } from "zod";
import { FeatureSchema, LineStringGeometrySchema, MultiLineStringSchema, PointGeometrySchema, PointSchema, PolygonGeometrySchema } from "./geojson";
import hat from "hat";

const LanguageSchema = z.enum(["en", "sv", "no", "fi", "nl"]);
type Language = z.infer<typeof LanguageSchema>
export const Languages = LanguageSchema.options

type LangReturnType<S extends string> = {
	[
	K in S
	| `${S}_${Language}`
	]: z.ZodString | z.ZodOptional<z.ZodString>
};

function lang<S extends string>(input: S, _default?: string): LangReturnType<S> {
	const langs: any = {
		[input]: z.string().default(_default ?? "").catch("")
	}
	for (const lang of LanguageSchema.options) {
		langs[`${input}_${lang}`] = z.string().optional()
	}
	return langs as LangReturnType<S>
}

/**
 * Input can be number, string or undefined. Output is number or undefined.
 * Used for textfields with numeric values, previous versions saved strings.
 */
export const InputNumber = z
	.coerce
	.string()
	.transform((value) => (value === '' || isNaN(parseFloat(value)) ? undefined : parseFloat(value)))


const ArrowSchema = z.object({
	distance: InputNumber.optional(),
	position: z.enum(["left", "right"]).default("left"),
})
export type ArrowData = z.infer<typeof ArrowSchema>

//Deprecated
const MultiColorSchema = z.object({
	color: z.string().default(""),
	distance: InputNumber.default("0"),
})
//Deprecated
export type MultiColorData = z.infer<typeof MultiColorSchema>

const RouteLegSchema = z.object({
	index: z.number(),
	...lang("label"),
	color: z.string(),
	id: z.string().catch(() => hat())
})
export type RouteLeg = z.infer<typeof RouteLegSchema>

export const RouteSchema = MultiLineStringSchema.extend({
	properties: z.object({
		...lang("label"),
		color: z.string().default("#e69138"),
		thickness: InputNumber.default("4"),
		...lang("startLabel", "START"),
		...lang("startDescription"),
		...lang("goalLabel", "FINISH"),
		...lang("goalDescription"),
		icon: z.string().default("start"),
		customInactiveColor: z.boolean().default(false),
		inactiveColor: z.string().default("#999"),
		customHeightColor: z.boolean().default(false),
		heightColor: z.string().default("#999"),

		//deprecated
		useMultiColor: z.boolean().default(false).optional(),
		//deprecated
		multiColors: z.array(MultiColorSchema).default([]).optional(),

		legs: z.array(RouteLegSchema).default([]),

		slug: z.string().optional(),
		heights: z.array(z.number()).default([]),
		trackLength: InputNumber.optional(),
		lineOffset: InputNumber.optional(),
		hideRoute: z.boolean().optional(),
		downloadGpx: z.boolean().optional(),
		hideElevation: z.boolean().optional(),
		heightMin: InputNumber.optional(),
		heightMax: InputNumber.optional(),
		hideGain: z.boolean().optional(),
		hideLoss: z.boolean().optional(),
		gain: InputNumber.optional(),
		loss: InputNumber.optional(),
		hideMaxAltitude: z.boolean().optional(),
		maxAltitude: InputNumber.optional(),
		arrows: z.array(ArrowSchema).default([]),
		heightHash: z.string().optional(),

		paceCalculation: z.object({
			enabled: z.boolean().default(false),
			groups: z.record(z.string(), z.object({
				type: z.enum(["running", "cycling", "swimming", "other"]).default("running"),
			})).catch({}),
			clockTime: z.boolean().default(true),
		}).default({
			groups: {}
		}),

		//unused, to be deleted
		customGain: z.string().or(z.number()).optional(),
		customLoss: z.string().or(z.number()).optional(),
	})/*.strict()*/
}).default({
	type: "Feature",
	id: hat() as string,
	geometry: {
		type: "MultiLineString",
		coordinates: []
	},
	properties: {}
})
export type Route = z.infer<typeof RouteSchema>

export const MarkerSchema = PointSchema.extend({
	id: z.string(),
	properties: z.object({
		...lang("label"),
		...lang("description"),
		imageName: z.string().optional(),
		imageUrl: z.string().optional(),
		imageFill: z.boolean().optional(),
		imageWidth: z.number().optional(),
		imageHeight: z.number().optional(),
		anchor: z.enum(["center", "top", "bottom", "left", "right", "top-left", "top-right", "bottom-left", "bottom-right"]).optional().catch(undefined),
		zoom: InputNumber.optional(),
		exclude: z.array(z.string()).optional().catch([]),
		slug: z.string().optional(),
		showInElevation: z.boolean().optional(),
		gpx: z.boolean().optional(),
	})/*.strict()*/
})

export type Marker = z.infer<typeof MarkerSchema>

const ComputedMarkerSchema = MarkerSchema.extend({
	properties: MarkerSchema.shape.properties.extend({
		__progress: z.array(z.number()).optional(),
		__generated: z.boolean().optional(),
		__markerVariation: z.string().optional(),
		__forceLabelAnchorRight: z.boolean().optional(),
		__zIndex: z.number().optional(),

		id: z.string().default("").optional(),
		__excluded: z.boolean().optional(),
		__categoryId: z.string().optional(),
		__angle: z.number().optional(),
		__iconOffset: z.array(z.number()).optional(),
		__labelOffset1: z.array(z.number()).optional(),
		__labelOffset2: z.array(z.number()).optional(),
		icon: z.string(),
		color: z.string(),
		textColor: z.string().optional(),

		// TODO: only used by LiveRenderer? refactor.
		text: z.string().optional(),
	})
})

export type ComputedMarker = z.infer<typeof ComputedMarkerSchema>

export const MarkerCategorySchema = z.object({
	id: z.string().default(""),
	icon: z.string().default(""),
	...lang("label"),
	color: z.string().default(""),
	markers: z.array(MarkerSchema).default([]).optional(),
})/*.strict()*/

export type MarkerCategory = z.infer<typeof MarkerCategorySchema>


export const ShapeSchema = FeatureSchema.extend({
	geometry: z.union([PolygonGeometrySchema, LineStringGeometrySchema, PointGeometrySchema]),
	properties: z.object({
		uid: z.string().default(""),
		color: z.string().default("#ccc"),
		outlineColor: z.string().default("#000"),
		outlineWidth: InputNumber.default("1"),
		outlineScale: z.boolean().default(false),
		zoom: InputNumber.optional(),
		shapeType: z.enum(["", "circle", "rectangle", "image"]).default("").catch(""),
		width: InputNumber.default("5"),
		height: InputNumber.default("5"),
		rotation: InputNumber.default("0"),
		slug: z.string().optional(),
		zoomable: z.boolean().optional(),
		...lang("zoomableText"),
		locked: z.boolean().optional(),
		exclude: z.array(z.string()).optional().catch([]),
		//text settings
		...lang("text"),
		textAnchor: z.enum(['center', 'center-fixed', 'left', 'right', 'top', 'bottom']).default('center').catch('center'),
		textRotation: InputNumber.default("0"),
		textZoom: InputNumber.optional(),
		// images
		imageName: z.string().default(""),
		imageUrl: z.string().default(""),
		imageWidth: z.number().optional(),
		imageHeight: z.number().optional(),
		worldSize: z.boolean().default(false),
		anchor: z.enum(["center", "top", "bottom", "left", "right", "top-left", "top-right", "bottom-left", "bottom-right"]).optional().catch(undefined),
		iconSizeStops: z.record(z.string(), z.number()).optional(),


	})/*.strict()*/
})
export type Shape = z.infer<typeof ShapeSchema>

const ComputedShapeSchema = ShapeSchema.extend({
	properties: ShapeSchema.shape.properties.extend({
		__generatedGeometry: z.boolean().optional(),
		__excluded: z.boolean().optional(),
		__imageId: z.string().nullable().optional(),
		__loadedWidth: z.number().optional(),
	})
})

export type ComputedShape = z.infer<typeof ComputedShapeSchema>

export const ShapeLayerSchema = z.object({
	id: z.string().default(""),
	name: z.string().default(""),
	shapes: z.array(ShapeSchema).default([]),
})

export type ShapeLayer = z.infer<typeof ShapeLayerSchema>

const ShapeRootLayerSchema = z.object({
	shapes: z.array(ShapeSchema.or(ShapeLayerSchema)).default([]),
})/*.strict()*/
export type ShapeRootLayer = z.infer<typeof ShapeRootLayerSchema>

export const MapSchema = z.object({
	id: z.string().default(""),
	name: z.string().default(""),
	routes: z.array(RouteSchema).default([]),
	markers: z.array(MarkerCategorySchema).default([]),
	shapeLayer: ShapeRootLayerSchema.default({}),
	userId: z.string().default(""),
	orgId: z.string().optional(),
	basemap: z.string().default(""), //TODO narrow down
	contourLines: z.boolean().default(false),
	satellite: z.boolean().default(false),
	markerLabels: z.boolean().default(true),
	markerVariation: z.enum(["default", "invert"]).default("default"),
	logo: z.object({
		imageName: z.string().default(""),
		imageUrl: z.string().default(""),
		fill: z.boolean().default(false),

	}).default({}),
	animate: z.boolean().default(true),
	// When false, routes other than the selected one are not drawn in the background.
	showInactiveRoutes: z.boolean().default(true),
	unit: z.enum(["km", "mi"]).default("km"),
	texts: z.object({
		language: LanguageSchema.or(z.array(z.object({ lang: LanguageSchema }))).default("en"),
		...lang("findInMap", ""),
		...lang("moreInfoHeader"),
		...lang("moreInfoContent"),
	})/*.strict()*/.default({}),
	center: z.tuple([z.number(), z.number()]).optional(),

	published: z.boolean().optional(),
	changed: z.string().optional(),

	editorState: z.object({
		closedItems: z.array(z.string()).default(['shapes']),
	}).default({}),

})/*.strict()*/
